import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, FileDown, Calendar, AlertCircle, TrendingUp, DollarSign, Package, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useStores } from '../hooks/useStores';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getStoreName, stores, storeOptions } = useStores();
  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedStore, setSelectedStore] = useState('all');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const COLORS = ['#D4F0A5', '#FADBB0', '#A78BFA', '#FCA5A5', '#93C5FD'];

  // Verificar permisos - Empleados no tienen acceso
  const isEmployee = user?.role === 'employee';

  useEffect(() => {
    if (isEmployee) {
      toast.error('No tienes acceso a esta sección');
      navigate('/');
    }
  }, [isEmployee, navigate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${API}/reports/data?period=${period}`;
      if (period === 'custom' && startDate && endDate) {
        url += `&start_date=${startDate}&end_date=${endDate}`;
      }
      if (selectedStore !== 'all') {
        url += `&store=${selectedStore}`;
      }

      const response = await axios.get(url);
      setReportData(response.data);
      toast.success('Reporte generado');
    } catch (error) {
      toast.error('Error al generar reporte');
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const currencySymbol = reportData.currency_symbol || '$';
    
    // Title
    doc.setFontSize(18);
    doc.text('Reporte de Ventas', 14, 22);
    
    // Period info
    doc.setFontSize(11);
    doc.text(`Período: ${reportData.period}`, 14, 32);
    doc.text(`Fecha inicio: ${reportData.start_date}`, 14, 38);
    doc.text(`Fecha fin: ${reportData.end_date}`, 14, 44);
    doc.text(`Timezone: ${reportData.timezone}`, 14, 50);
    
    // Summary
    doc.setFontSize(14);
    doc.text('Resumen General', 14, 60);
    
    const summary = reportData.summary || {};
    const summaryData = [
      ['Concepto', 'Monto'],
      ['Ventas POS', `${currencySymbol}${Math.round(summary.pos_sales || 0).toLocaleString('es-CL')}`],
      ['Ventas Ecommerce', `${currencySymbol}${Math.round(summary.ecommerce_sales || 0).toLocaleString('es-CL')}`],
      ['Total Ventas', `${currencySymbol}${Math.round(summary.total_sales || 0).toLocaleString('es-CL')}`],
      ['Total Gastos', `${currencySymbol}${Math.round(summary.total_expenses || 0).toLocaleString('es-CL')}`],
      ['Otros Ingresos', `${currencySymbol}${Math.round(summary.total_income || 0).toLocaleString('es-CL')}`],
      ['Utilidad Neta', `${currencySymbol}${Math.round(summary.net_profit || 0).toLocaleString('es-CL')}`],
    ];
    
    doc.autoTable({
      startY: 64,
      head: [summaryData[0]],
      body: summaryData.slice(1),
    });
    
    // Sales detail
    if (reportData.sales.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Detalle de Ventas', 14, 22);
      
      const salesData = reportData.sales.map(sale => [
        new Date(sale.created_at).toLocaleDateString('es-CL'),
        sale.product_name || 'N/A',
        getStoreName(sale.store_id || sale.store),
        sale.quantity,
        `$${sale.price.toLocaleString('es-CL')}`,
        `$${sale.total.toLocaleString('es-CL')}`,
        sale.payment_method,
        sale.has_tax ? 'Sí' : 'No'
      ]);
      
      doc.autoTable({
        startY: 26,
        head: [['Fecha', 'Producto', 'Tienda/Caja', 'Cant.', 'Precio', 'Total', 'Pago', 'IVA']],
        body: salesData,
        styles: { fontSize: 8 }
      });
    }
    
    doc.save(`reporte-${period}-${Date.now()}.pdf`);
    toast.success('PDF descargado');
  };

  const downloadExcel = () => {
    if (!reportData) return;

    const wb = XLSX.utils.book_new();
    const currencySymbol = reportData.currency_symbol || '$';
    const summary = reportData.summary || {};
    
    // Summary sheet
    const summaryData = [
      ['REPORTE DE VENTAS'],
      ['Período', reportData.period],
      ['Fecha Inicio', reportData.start_date],
      ['Fecha Fin', reportData.end_date],
      ['Timezone', reportData.timezone],
      ['Moneda', reportData.currency_code],
      [],
      ['RESUMEN GENERAL'],
      ['Concepto', 'Monto'],
      ['Ventas POS', summary.pos_sales || 0],
      ['Ventas Ecommerce', summary.ecommerce_sales || 0],
      ['Total Ventas', summary.total_sales || 0],
      ['Total Gastos', summary.total_expenses || 0],
      ['Otros Ingresos', summary.total_income || 0],
      ['Utilidad Neta', summary.net_profit || 0],
    ];
    
    const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWS, 'Resumen');
    
    // Sales detail sheet
    if (reportData.sales.length > 0) {
      const salesData = reportData.sales.map(sale => ({
        Fecha: new Date(sale.created_at).toLocaleDateString('es-CL'),
        Producto: sale.product_name || 'N/A',
        'Tienda/Caja': getStoreName(sale.store_id || sale.store),
        Cantidad: sale.quantity,
        Precio: sale.price,
        Total: sale.total,
        'Método Pago': sale.payment_method,
        'Con IVA': sale.has_tax ? 'Sí' : 'No',
        Cliente: sale.customer || ''
      }));
      
      const salesWS = XLSX.utils.json_to_sheet(salesData);
      XLSX.utils.book_append_sheet(wb, salesWS, 'Ventas');
    }
    
    // Expenses sheet
    if (reportData.expenses.length > 0) {
      const expensesData = reportData.expenses.map(exp => ({
        Fecha: new Date(exp.created_at).toLocaleDateString('es-CL'),
        Descripción: exp.description,
        Monto: exp.amount,
        Categoría: exp.category,
        Usuario: exp.user_name
      }));
      
      const expensesWS = XLSX.utils.json_to_sheet(expensesData);
      XLSX.utils.book_append_sheet(wb, expensesWS, 'Egresos');
    }
    
    XLSX.writeFile(wb, `reporte-${period}-${Date.now()}.xlsx`);
    toast.success('Excel descargado');
  };

  // Mostrar mensaje de acceso denegado para empleados
  if (isEmployee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
        <div className="max-w-2xl mx-auto mt-20">
          <div 
            className="bg-white border-2 border-slate-900 rounded-xl p-8 text-center"
            style={{ boxShadow: '8px 8px 0px 0px rgba(15,23,42,1)' }}
          >
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-2xl font-bold mb-2">Acceso Denegado</h2>
            <p className="text-slate-600 mb-6">
              No tienes permisos para acceder a la sección de Reportes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" style={{ backgroundColor: '#F4F4F0', minHeight: '100vh' }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 
              className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Reportes
            </h1>
            <p className="text-base font-medium text-slate-600">Genera reportes descargables</p>
          </div>
        </div>
      </div>

      {/* Report Generator */}
      <div 
        className="bg-white border-2 border-slate-900 rounded-xl p-6 md:p-8 mb-6"
        style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
      >
        <h2 className="text-xl font-bold mb-6">Generar Reporte</h2>
        
        {/* Period Selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {['day', 'week', 'month', 'custom'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-3 rounded-xl font-bold border-2 border-slate-900 transition-all ${
                period === p ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 hover:bg-slate-50'
              }`}
              data-testid={`period-${p}-btn`}
            >
              {p === 'day' && 'Hoy'}
              {p === 'week' && 'Esta Semana'}
              {p === 'month' && 'Este Mes'}
              {p === 'custom' && 'Personalizado'}
            </button>
          ))}
        </div>

        {/* Store Filter */}
        <div className="mb-6">
          <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
            Filtrar por Tienda/Caja
          </label>
          <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${stores.length + 1}, minmax(0, 1fr))` }}>
            <button
              onClick={() => setSelectedStore('all')}
              className={`px-4 py-3 rounded-xl font-bold border-2 border-slate-900 transition-all ${
                selectedStore === 'all' ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
              }`}
            >
              Todas
            </button>
            {stores.map((store) => (
              <button
                key={store.id}
                onClick={() => setSelectedStore(store.id)}
                className={`px-4 py-3 rounded-xl font-bold border-2 border-slate-900 transition-all ${
                  selectedStore === store.id ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
                }`}
              >
                {store.name}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Date Range */}
        {period === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold tracking-widest uppercase text-slate-500 mb-2">
                Fecha Fin
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-white border-2 border-slate-900 rounded-xl px-4 py-3 font-medium"
                data-testid="end-date-input"
              />
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={fetchReport}
          disabled={loading || (period === 'custom' && (!startDate || !endDate))}
          className="w-full text-slate-900 border-2 border-slate-900 rounded-xl px-6 py-4 font-bold transition-all"
          style={{
            backgroundColor: '#D4F0A5',
            boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)'
          }}
          data-testid="generate-report-btn"
        >
          <Calendar className="inline w-5 h-5 mr-2" />
          {loading ? 'Generando...' : 'Generar Reporte'}
        </button>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div 
              className="bg-white border-4 border-slate-900 rounded-xl p-5"
              style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-8 h-8 text-green-600" />
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <h3 className="text-xs font-bold tracking-widest uppercase text-slate-500">Ventas Totales</h3>
              <p className="text-3xl font-black font-mono text-slate-900">
                {reportData.currency_symbol}{Math.round(reportData.summary?.total_sales || 0).toLocaleString('es-CL')}
              </p>
              <div className="mt-2 pt-2 border-t border-slate-200">
                <p className="text-xs text-slate-600">
                  POS: {reportData.currency_symbol}{Math.round(reportData.summary?.pos_sales || 0).toLocaleString('es-CL')}
                </p>
                <p className="text-xs text-slate-600">
                  Ecommerce: {reportData.currency_symbol}{Math.round(reportData.summary?.ecommerce_sales || 0).toLocaleString('es-CL')}
                </p>
              </div>
            </div>

            <div 
              className="bg-white border-4 border-slate-900 rounded-xl p-5"
              style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xs font-bold tracking-widest uppercase text-slate-500">Gastos</h3>
              <p className="text-3xl font-black font-mono text-slate-900">
                {reportData.currency_symbol}{Math.round(reportData.summary?.total_expenses || 0).toLocaleString('es-CL')}
              </p>
            </div>

            <div 
              className="bg-white border-4 border-slate-900 rounded-xl p-5"
              style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <Package className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xs font-bold tracking-widest uppercase text-slate-500">Utilidad Neta</h3>
              <p className="text-3xl font-black font-mono text-slate-900">
                {reportData.currency_symbol}{Math.round(reportData.summary?.net_profit || 0).toLocaleString('es-CL')}
              </p>
            </div>

            <div 
              className="bg-white border-4 border-slate-900 rounded-xl p-5"
              style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
            >
              <div className="flex items-center justify-between mb-2">
                <ShoppingCart className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-xs font-bold tracking-widest uppercase text-slate-500">Transacciones</h3>
              <p className="text-3xl font-black font-mono text-slate-900">
                {reportData.transactions_count || 0}
              </p>
            </div>
          </div>

          {/* Charts */}
          {reportData.daily_sales_chart && reportData.daily_sales_chart.length > 0 && (
            <div 
              className="bg-white border-4 border-slate-900 rounded-xl p-6"
              style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
            >
              <h2 className="text-xl font-bold mb-6">📈 Ventas Diarias</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={reportData.daily_sales_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#475569"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    stroke="#475569"
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '2px solid #0f172a',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#0f172a" 
                    strokeWidth={3}
                    dot={{ fill: '#D4F0A5', stroke: '#0f172a', strokeWidth: 2, r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Products and Payment Methods */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Products */}
            {reportData.top_products && reportData.top_products.length > 0 && (
              <div 
                className="bg-white border-4 border-slate-900 rounded-xl p-6"
                style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
              >
                <h2 className="text-xl font-bold mb-6">🏆 Productos Más Vendidos</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.top_products}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total" fill="#D4F0A5" stroke="#0f172a" strokeWidth={2} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Payment Methods */}
            {reportData.payment_methods && Object.keys(reportData.payment_methods).length > 0 && (
              <div 
                className="bg-white border-4 border-slate-900 rounded-xl p-6"
                style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
              >
                <h2 className="text-xl font-bold mb-6">💳 Métodos de Pago</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={Object.entries(reportData.payment_methods).map(([key, value]) => ({
                        name: key,
                        value: value.total
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="#0f172a"
                      strokeWidth={2}
                    >
                      {Object.keys(reportData.payment_methods).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Store Comparison */}
          <div 
            className="bg-white border-4 border-slate-900 rounded-xl p-6"
            style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
          >
            <h2 className="text-xl font-bold mb-6">🏪 Comparación por Tienda</h2>
            <div className={`grid gap-6`} style={{ gridTemplateColumns: `repeat(${stores.length}, minmax(0, 1fr))` }}>
              {stores.map((store, index) => {
                const byStore = reportData.by_store || {};
                const storeData = byStore[store.id] || byStore[store.code] || { sales: 0, cost: 0, profit: 0 };

                return (
                  <div
                    key={store.id}
                    className="p-4 border-2 border-slate-900 rounded-xl"
                    style={{ backgroundColor: store.color || COLORS[index % COLORS.length] }}
                  >
                    <h3 className="font-bold text-sm uppercase mb-2">{store.name}</h3>
                    <p className="text-3xl font-black font-mono mb-2">
                      ${Math.round(storeData.sales || 0).toLocaleString('es-CL')}
                    </p>
                    <p className="text-sm text-green-700 font-bold">
                      Utilidad: ${Math.round(storeData.profit || 0).toLocaleString('es-CL')}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Download Buttons */}
          <div 
            className="bg-white border-4 border-slate-900 rounded-xl p-6"
            style={{ boxShadow: '6px 6px 0px 0px rgba(15,23,42,1)' }}
          >
            <h2 className="text-xl font-bold mb-4">Descargar Reporte</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={downloadPDF}
                className="flex-1 bg-white text-slate-900 border-2 border-slate-900 rounded-xl px-6 py-4 font-bold transition-all hover:bg-slate-50"
                style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                data-testid="download-pdf-btn"
              >
                <FileDown className="inline w-5 h-5 mr-2" />
                Descargar PDF
              </button>
              <button
                onClick={downloadExcel}
                className="flex-1 bg-white text-slate-900 border-2 border-slate-900 rounded-xl px-6 py-4 font-bold transition-all hover:bg-slate-50"
                style={{ boxShadow: '4px 4px 0px 0px rgba(15,23,42,1)' }}
                data-testid="download-excel-btn"
              >
                <FileDown className="inline w-5 h-5 mr-2" />
                Descargar Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
